import { DeviceEvent } from "../models/DeviceEvent";
import { DeviceEventCriteria, DeviceEventCriteriaHelper } from "../models/DeviceEventCriteria";
import OCGatewayClient from "../client/OCGatewayClient";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { gatewayRealtimeQueryOptions, prependInfiniteQueryItem, updateMatchingQueries } from "./gatewayQueryUtils";

export interface IDevice {
    deviceId: string;
    description?: string;
}

let subscribed = false;

export function useEventsQuery(criteria?: DeviceEventCriteria) {
    const queryClient = useQueryClient();

    const query = useInfiniteQuery({
        queryKey: ['getEvents', criteria],
        queryFn: async ({pageParam : beforeGlobalId}): Promise<any> => {
            const requestParams: any = {
                subscribe: !beforeGlobalId,
                // Spread all criteria properties, filtering out undefined values
                ...Object.fromEntries(
                    Object.entries(criteria || {}).filter(([_, value]) => value !== undefined)
                )
            };

            if (beforeGlobalId) {
                requestParams.beforeGlobalId = beforeGlobalId;
            }

            return OCGatewayClient.request('getEvents', requestParams) as Promise<DeviceEvent[]>;
        },
        ...gatewayRealtimeQueryOptions,
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams) => {
            return lastPage.length ? lastPage[lastPage.length - 1].globalId : undefined;
        },
        initialPageParam: undefined
    });

    if(!subscribed) {
        OCGatewayClient.socket.on('userEventUpdate', (data: any) => {
            // Update getEvents queries, checking criteria for each query
            updateMatchingQueries(queryClient, ['getEvents'], (queryKey) => {
                const queryCriteria = queryKey[1] as DeviceEventCriteria | undefined;

                queryClient.setQueryData(queryKey, (queryData: any) => {
                    const pages: Array<DeviceEvent[]> = queryData?.pages;

                    if(!pages?.length) {
                        console.log('userEventUpdate (ignored)', data);
                        return queryData;
                    }

                    if(data.type === 'create')
                    {
                        // Check if the new event matches this query's criteria
                        if (DeviceEventCriteriaHelper.eventMatches(data.body, queryCriteria)) {
                            return prependInfiniteQueryItem<DeviceEvent>(queryData, data.body, 'userEventUpdate');
                        } else {
                            console.log('userEventUpdate (filtered out)', data);
                            return queryData;
                        }
                    }

                    console.log('userEventUpdate (no match)', data);
                    return queryData;
                });
            });
        });

        subscribed = true;
    }

    return query;
}
